import { useMutation } from '@tanstack/react-query';
import { ComponentProps } from 'preact';
import { useEffect, useId, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import AgeGroupSelector, { defaultTheme } from '@/components/AgeGroupSelector';
import PersonaSelector from '@/components/PersonaSelector';
import { patchUserMutationFn } from '@/queries/users';
import { useSelector } from '@/redux/helper';
import { selectIsProfileCompleted } from '@/redux/selectors/users';
import { updateUserProperty } from '@/redux/slices/user/actions';
import { AgeGroup, ExperienceOption } from '@/redux/slices/user/types';
import { ErrorsMap, toErrorsMap } from '@/shared/fetch';

import {
  ButtonContainer,
  Content,
  FieldContainer,
  FieldError,
  FieldInput,
  FieldLabel,
  Header,
  HeaderTitle,
  PrimaryButton,
  Section,
} from './Section';

type FormErrors = ErrorsMap<{ name?: unknown; ageGroup?: unknown; persona?: unknown }>;

const selectTheme = {
  ...defaultTheme,
  select: {
    ...defaultTheme.select,
    control: {
      ...defaultTheme.select.control,
      extend: {
        ...defaultTheme.select.control.extend,
        borderColor: '#d1d5db',
      },
    },
  },
};

const selectErrorTheme = {
  ...defaultTheme,
  select: {
    ...defaultTheme.select,
    control: {
      ...defaultTheme.select.control,
      extend: {
        ...defaultTheme.select.control.extend,
        borderColor: '#ef4444',
      },
    },
  },
};

const AboutMeSection = () => {
  const id = useId();
  const dispatch = useDispatch();
  const isProfileCompleted = useSelector(selectIsProfileCompleted);
  const existingName = useSelector((state) => state.user.name);
  const existingAgeGroup = useSelector((state) => state.user.ageGroup);
  const existingPersona = useSelector((state) => state.user.persona);
  const [name, setName] = useState(existingName ?? '');
  const [ageGroup, setAgeGroup] = useState<AgeGroup | null>(existingAgeGroup ?? null);
  const [persona, setPersona] = useState<ExperienceOption | null>(existingPersona ?? null);
  const [errors, setErrors] = useState<FormErrors>({});

  const {
    mutate: updateUser,
    isPending,
    isSuccess,
  } = useMutation({
    mutationFn: patchUserMutationFn,
    onSuccess: () => {
      dispatch(updateUserProperty({ name, ageGroup, persona }));
    },
    onError: (error) => {
      console.error(error);
      setErrors(toErrorsMap<FormErrors>(error));
    },
  });

  const onSubmit: ComponentProps<'form'>['onSubmit'] = (e) => {
    e.preventDefault();
    const newErrors: typeof errors = {};

    if (!name) newErrors.name = ['Name is required'];
    if (!ageGroup) newErrors.ageGroup = ['Age is required'];
    if (!persona) newErrors.persona = ['Please select how you use zoolife'];

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    updateUser({
      name,
      ageGroup: ageGroup as AgeGroup,
      persona: persona as ExperienceOption,
    });
  };

  useEffect(() => {
    setAgeGroup(existingAgeGroup ?? null);
  }, [existingAgeGroup]);

  useEffect(() => {
    setName(existingName ?? '');
  }, [existingName]);

  useEffect(() => {
    setPersona(existingPersona ?? null);
  }, [existingPersona]);

  return (
    <Section>
      <Header>
        <div className="flex items-center justify-between">
          <HeaderTitle>About Me</HeaderTitle>
          {!isProfileCompleted && (
            <div>
              <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px]/[1rem] font-medium uppercase text-rose-500">
                Complete your profile
              </span>
            </div>
          )}
        </div>
        <p className="preflight preflight-p mt-1 text-sm text-stone-500">
          Tell us a little about yourself so that we can personalize your experience
        </p>
      </Header>
      <Content>
        <form onSubmit={onSubmit}>
          <FieldContainer className="desktop:max-w-[50%]">
            <FieldLabel htmlFor={`${id}-name`}>Name</FieldLabel>
            <FieldInput
              id={`${id}-name`}
              name="name"
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
            />
            {errors.name && <FieldError>{errors.name.join(' ')}</FieldError>}
          </FieldContainer>
          <FieldContainer className="mt-5 desktop:max-w-[50%]">
            <FieldLabel htmlFor={`${id}-age`}>Age</FieldLabel>
            <div className="desktop:max-w-[400px]">
              <AgeGroupSelector
                id={`${id}-age`}
                required
                theme={errors.ageGroup ? selectErrorTheme : selectTheme}
                value={ageGroup as string}
                className="!px-3 !py-2 !text-base !font-normal"
                onChange={({ option }) => {
                  setAgeGroup(option.value as AgeGroup);
                }}
              />
            </div>
            {errors.ageGroup && <FieldError>{errors.ageGroup.join(' ')}</FieldError>}
          </FieldContainer>
          <FieldContainer className="mt-10">
            <h5 className={`preflight preflight-h5 ${FieldLabel.defaults.className}`}>How I use zoolife</h5>
            <PersonaSelector value={persona} onChange={setPersona} className="grid grid-cols-1 gap-2 desktop:grid-cols-3" />
            {errors.persona && <FieldError>{errors.persona.join(' ')}</FieldError>}
          </FieldContainer>
          {errors['*'] && <FieldError className="mt-2 desktop:text-right">{errors['*'].join(' ')}</FieldError>}
          <ButtonContainer className="mt-6">
            <PrimaryButton type="submit" disabled={isPending}>
              {isPending ? 'Updating...' : isSuccess ? 'Updated ✔' : 'Update'}
            </PrimaryButton>
          </ButtonContainer>
        </form>
      </Content>
    </Section>
  );
};

export default AboutMeSection;
